import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarInset, SidebarProvider } from '@/components/shadcn/sidebar';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { AgentPanel } from '@/components/agent/AgentPanel';
import { WriteBlockedDialog } from '@/components/billing/WriteBlockedDialog';
import { TrialEndingBand } from '@/components/billing/TrialEndingBand';
import { AgentWorkspace } from '@/components/agent/AgentWorkspace';
import { useSetupHandoff } from '@/components/agent/useSetupHandoff';
import { useAppMode } from '@/contexts/AgentModeContext';

/** Kept from the previous shell so nobody loses their collapsed sidebar. */
const COLLAPSED_KEY = 'jf_sidebar_collapsed';

/**
 * The app shell: a rail on the window background, and the product inside a
 * floating card.
 *
 * WHERE THE SCROLL LIVES, AND WHY IT MATTERS
 *
 * The obvious arrangement — let the page scroll and let the card grow with it —
 * looks right until the content is taller than the window. Then the card's
 * bottom edge is somewhere down the DOCUMENT rather than at the bottom of the
 * screen, so on a long page (Billing, a full invoice list) you scroll and the
 * box simply has no bottom. It stops reading as a box at all.
 *
 * So the shell is exactly one viewport tall and the window never scrolls. The
 * card fills it, and THE SCROLL IS INSIDE THE CARD. The border stays where it
 * is, the topbar stays pinned to the top of the panel, and only the content
 * moves — which is what keeps it looking like a box on the tenth screen of a
 * table.
 *
 * `min-h-0` is the load-bearing part: a flex child defaults to
 * `min-height: auto`, so it refuses to shrink below its content and the
 * overflow container grows instead of scrolling. Without it this silently
 * reverts to the bug it exists to fix.
 */
export function Shell() {
  const { isAgent, wantsDock, clearDock } = useAppMode();

  /* A workspace that has just been created opens in agent mode, on the thread
     the server greeted them in. Once, and only while the greeting is still
     unanswered — see useSetupHandoff. */
  useSetupHandoff();

  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) !== '1';
    } catch {
      return true;
    }
  });

  /**
   * Only a DELIBERATE collapse is remembered.
   *
   * Persisting every change would mean opening the agent once — which collapses
   * the rail automatically below — silently rewrites the preference, and the
   * sidebar is still collapsed tomorrow for a reason the user never chose. So
   * writing to storage happens in the user-driven handler, not in an effect
   * that cannot tell the two apart.
   */
  const remember = (next: boolean) => {
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '0' : '1');
    } catch {
      /* ignore */
    }
  };

  const onOpenChange = useCallback((next: boolean) => {
    autoCollapsed.current = false; // they have taken it back
    remember(next);
    setOpen(next);
  }, []);

  /**
   * The agent rail is a SIBLING of the content card, not a child of it.
   *
   * A drawer rendered inside the card would be constrained by the card's
   * padding and would scroll with the page; as a sibling it is full height,
   * outside the box, and the content simply narrows to make room — which is
   * what lets you keep reading an invoice while you ask about it.
   */
  const [agentOpen, setAgentOpen] = useState(false);

  /** True only while WE hold the rail closed, so we know whether to restore it. */
  const autoCollapsed = useRef(false);

  /**
   * THE CONVERSATION MOVES. It does not close and reopen somewhere else.
   *
   * Leaving agent mode by clicking a page used to drop straight to manual, and
   * since the rail is a separate piece of state that was still shut, the chat
   * simply vanished — reported, correctly, as "it lost my conversation". The
   * thread was never gone (useAgentChat re-reads it per workspace), but nothing
   * was showing it. `dockChat()` in the sidebar raises this flag; catching it
   * here is what puts the same thread in the rail beside the page they asked
   * for.
   *
   * The flag doubles as the panel's `handoff` hint. Its lifetime is exactly the
   * window the panel needs: it is already set on the frame the rail mounts
   * closed — the frame whose painted styles the opening transition starts from
   * — and it is cleared in the same breath as the open. So the panel knows to
   * arrive from the middle of the screen rather than from the window edge,
   * without a second piece of state to keep in step with this one.
   */
  useEffect(() => {
    if (!wantsDock) return;
    /*
      Opened on the NEXT frame, not this one.

      The panel animates its width up from zero, and a transition needs that
      zero to have been painted. Setting `open` in the commit that also mounts
      the panel gives the browser a single style to resolve, no start value and
      no animation — the rail would just appear, which is the "two separate
      things happened" reading this whole path exists to avoid. Clearing the
      dock flag rides along inside the frame, because doing it here would change
      this effect's dependencies and the cleanup would cancel the request before
      it ever ran.
    */
    const frame = requestAnimationFrame(() => {
      setAgentOpen(true);
      clearDock();
    });
    return () => cancelAnimationFrame(frame);
  }, [wantsDock, clearDock]);

  /**
   * Opening the agent collapses the navigation rail to icons, and closing it
   * puts the rail back.
   *
   * Two rails plus the content is too much on a laptop — something has to give,
   * and the navigation is the part you are not using at the moment you are
   * talking to the agent.
   *
   * Restoring is conditional on purpose. If the rail was ALREADY collapsed the
   * agent must not expand it on close, and if the user expands it themselves
   * while the agent is open (`onOpenChange` clears the flag) that choice wins —
   * an automatic layout must never overrule a deliberate one.
   */
  useEffect(() => {
    if (agentOpen) {
      if (open) {
        autoCollapsed.current = true;
        setOpen(false);
      }
    } else if (autoCollapsed.current) {
      autoCollapsed.current = false;
      setOpen(true);
    }
    // `open` is deliberately not a dependency: this reacts to the AGENT
    // toggling, and including it would re-collapse the rail the instant the
    // user expanded it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentOpen]);

  return (
    <SidebarProvider
      open={open}
      onOpenChange={onOpenChange}
      className="h-svh min-h-svh overflow-hidden"
      /*
        THE NARROW RAIL IS WIDER THAN SHADCN'S, AND IT HAS TO BE.

        The stock 3rem fits a 16px glyph with 8px either side and nothing else,
        because the stock design drops the words when it collapses. Ours keeps
        them, in small type under the icon, which is the only thing that makes a
        collapsed rail navigable rather than a memory test. 4.75rem is what
        "Purchases" needs to arrive as "Purchas..." rather than as "Pu...".

        It is set here rather than on the Sidebar because the width feeds TWO
        elements inside that component, the fixed rail and the spacer that
        reserves its place in the layout, and only the rail takes a style prop.
        Overriding one of them and not the other is how the content ends up
        underneath the navigation.
      */
      style={{ '--sidebar-width-icon': '4.75rem' } as CSSProperties}
    >
      <Sidebar />

      {/*
        The floating panel. shadcn's inset variant supplies the margin, radius
        and shadow; the hairline is added here — without a border the card and
        the page read as one surface in light mode, where the two backgrounds
        are only a few percent apart.

        `overflow-hidden` also keeps the topbar's blur from painting over the
        rounded corners and squaring them off.
      */}
      <SidebarInset className="relative min-h-0 min-w-0 overflow-hidden border border-border/70 shadow-[var(--elev-2)]">
        {isAgent ? (
          /*
            Agent mode replaces the CARD's contents, not the shell.

            The rail, the workspace switcher and the floating panel all stay
            exactly where they were — switching modes changes what you are
            looking at, not where anything is. Routing to a separate full-screen
            page would move every piece of furniture at once, which is how a
            mode toggle starts feeling like a different application.
          */
          <AgentWorkspace />
        ) : (
          <>
            <Topbar agentOpen={agentOpen} onAgentToggle={setAgentOpen} />

            {/* Under the bar and above the page, so it is read on the way in
                and scrolls away with nothing. Renders nothing at all until the
                trial is inside its last week. */}
            <TrialEndingBand />

            {/*
              The scroll container. `overscroll-contain` stops a trackpad flick
              at the end of a list from rubber-banding the whole shell away from
              the window edge — there is nothing behind this to scroll.
            */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {/* Measure, not width: the content column stops at 1400px so a
                  wide monitor gets margins instead of 2000px-long table rows. */}
              {/*
                `data-page-shell` is the handle a full-bleed screen grabs.

                Every ordinary page wants this padding and this 1400px measure:
                a wide monitor should give margins rather than table rows two
                thousand pixels long. A list-and-detail screen wants the
                opposite, because an email client with a card floating inside a
                padded column is a box inside a box inside a box, which is
                exactly what it looked like. SplitView finds this element and
                flattens it while it is mounted, then puts it back.
              */}
              <div
                data-page-shell
                className="mx-auto w-full max-w-[1400px] min-w-0 px-4 pt-7 pb-24 sm:px-7 lg:px-9 lg:pt-9"
              >
                <Outlet />
              </div>
            </div>
          </>
        )}
      </SidebarInset>

      {/* The rail is manual mode's agent. In agent mode the conversation IS the
          screen, so a second copy of it beside itself would be nonsense. */}
      {isAgent ? null : (
        <AgentPanel open={agentOpen} onOpenChange={setAgentOpen} handoff={wantsDock} />
      )}

      {/* Last child of the shell, so it sits above the rail and any sheet.
          Renders nothing at all while the workspace can write. */}
      <WriteBlockedDialog />
    </SidebarProvider>
  );
}
